import { useCallback, useLayoutEffect, useRef } from 'react';

import { Theme } from './constants';
import { ThemeState, themeStoreSelector, useThemeStore } from './themeStore';

interface ThemeData
  extends Omit<ThemeState, 'setForcedTheme' | 'setThemeDisabled'> {
  isDarkTheme: boolean;
}

export const getThemeData = (): ThemeData => {
  const { theme, setTheme, toggleTheme, themeDisabled, isNewThemeActive } =
    useThemeStore.getState();

  return {
    theme,
    setTheme,
    toggleTheme,
    isNewThemeActive,
    themeDisabled,
    isDarkTheme: theme === Theme.DARK,
  };
};

export const useTheme = (): ThemeData => {
  const theme = useThemeStore(themeStoreSelector.theme);
  const setTheme = useThemeStore(themeStoreSelector.setTheme);
  const toggleTheme = useThemeStore(themeStoreSelector.toggleTheme);
  const themeDisabled = useThemeStore(themeStoreSelector.themeDisabled);
  const isNewThemeActive = useThemeStore(themeStoreSelector.isNewThemeActive);

  return {
    setTheme,
    toggleTheme,
    isNewThemeActive,
    theme,
    themeDisabled,
    isDarkTheme: theme === Theme.DARK,
  };
};

export const useNewThemeActive = (): boolean =>
  useThemeStore(themeStoreSelector.isNewThemeActive);

export const getIsNewThemeActive = () =>
  useThemeStore.getState().isNewThemeActive;

export const useForcedTheme = (
  forcedTheme?: Theme
): { reset: () => void; applyForcedTheme: (theme?: Theme) => void } => {
  const theme = useThemeStore(themeStoreSelector.theme);
  const setForcedTheme = useThemeStore(themeStoreSelector.setForcedTheme);
  const setThemeDisabled = useThemeStore(themeStoreSelector.setThemeDisabled);
  const savedTheme = useRef<Theme>(theme);

  const reset = useCallback(() => {
    setForcedTheme(savedTheme.current);
    setThemeDisabled(false);
  }, [setForcedTheme, setThemeDisabled]);

  const applyForcedTheme = useCallback(
    (theme?: Theme) => {
      const newTheme = theme ?? forcedTheme;
      if (!newTheme) {
        return;
      }

      setForcedTheme(newTheme);
    },
    [forcedTheme, setForcedTheme]
  );

  useLayoutEffect(() => {
    setThemeDisabled(true);

    if (savedTheme.current === forcedTheme) {
      return (): void => setThemeDisabled(false);
    }

    if (forcedTheme) {
      applyForcedTheme(forcedTheme);
    }

    return reset;
  }, [forcedTheme, applyForcedTheme, reset, setThemeDisabled]);

  return { reset, applyForcedTheme };
};
